import React from 'react';
import { createPortal } from 'react-dom';

interface AlertProps {
  type?: 'success' | 'error' | 'warning' | 'info' | 'primary';
  message?: React.ReactNode;
  title?: string;
  onClose?: () => void;
  show?: boolean;
  dismissible?: boolean;
  children?: React.ReactNode;
  confirmable?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
  onOk?: () => void;
  showOkButton?: boolean;
  confirmText?: string;
  cancelText?: string;
  okText?: string;
  confirmVariant?: string;
  cancelVariant?: string;
  okVariant?: string;
}

const Alert: React.FC<AlertProps> = ({ 
  type = 'info', 
  message, 
  title,
  onClose, 
  show = false,
  dismissible = true,
  children,
  confirmable = false,
  onConfirm,
  onCancel,
  onOk,
  showOkButton = false,
  confirmText = 'Sim',
  cancelText = 'Não',
  okText = 'OK',
  confirmVariant = 'btn-primary',
  cancelVariant = 'btn-secondary',
  okVariant = 'btn-primary'
}) => {
  if (!show) return null;

  const alertConfig = {
    success: {
      className: 'alert-success',
      icon: 'fas fa-check-circle',
      title: 'Sucesso',
      bgClass: 'bg-gold text-white'
    },
    error: {
      className: 'alert-danger',
      icon: 'fas fa-exclamation-circle',
      title: 'Erro',
      bgClass: 'bg-danger text-white'
    },
    warning: {
      className: 'alert-warning',
      icon: 'fas fa-exclamation-triangle',
      title: 'Atenção',
      bgClass: 'bg-gold text-white'
    },
    info: {
      className: 'alert-info',
      icon: 'fas fa-info-circle',
      title: 'Informação',
      bgClass: 'bg-gold text-white'
    },
    primary: {
      className: 'alert-primary',
      icon: 'fas fa-bell',
      title: 'Notificação',
      bgClass: 'bg-gold text-white'
    }
  };

  const config = alertConfig[type] || alertConfig.info;

  const handleConfirm = () => {
    onConfirm?.();
    onClose?.();
  };

  const handleCancel = () => {
    onCancel?.();
    onClose?.();
  };

  const handleOk = () => {
    onOk?.();
    onClose?.();
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && dismissible) {
      onClose?.();
    }
  };

  const modalContent = (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        onClick={handleBackdropClick}
      ></div>
      
      {/* Modal */}
      <div 
        className="modal fade show d-block" 
        tabIndex={-1} 
        role="dialog"
        onClick={handleBackdropClick}
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            {/* Header */}
            <div className={`modal-header ${config.bgClass}`}>
              <h5 className="modal-title">
                <i className={`${config.icon} me-2`}></i>
                {title || config.title}
              </h5>
              {dismissible && (
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={onClose}
                  aria-label="Fechar"
                ></button>
              )}
            </div>
            
            {/* Body */}
            <div className="modal-body">
              <div className="alert-message">
                {message || children}
              </div>
            </div>
            
            {/* Footer */}
            <div className="modal-footer">
              {confirmable ? (
                <>
                  <button 
                    type="button" 
                    className={`btn ${cancelVariant}`}
                    onClick={handleCancel}
                  >
                    {cancelText}
                  </button>
                  <button 
                    type="button" 
                    className={`btn ${confirmVariant}`}
                    onClick={handleConfirm}
                  >
                    {confirmText}
                  </button>
                </>
              ) : showOkButton ? (
                <button 
                  type="button" 
                  className={`btn ${okVariant}`}
                  onClick={handleOk}
                >
                  {okText}
                </button>
              ) : (
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={onClose}
                >
                  Fechar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default Alert;
